import { fetchAuthSession } from "aws-amplify/auth";
import { getApiBaseUrl } from "./env.js";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | undefined,
    message: string,
    public readonly responseBody?: unknown,
  ) {
    // message is the server-supplied human message — kept as-is so screens
    // can render it directly. status + code stay as properties for branching.
    // responseBody carries the parsed JSON envelope (incl. `current` on 409
    // VERSION_CONFLICT) so callers can recover without a second fetch.
    super(message);
    this.name = "ApiError";
  }
}

// Use in catch blocks to render user-facing strings. Trust messages from sources
// known to be user-curated (our API 4xx, Amplify auth errors); collapse the
// engineer-y ones (5xx, network drop, non-Error throws) to a safe fallback.
export function friendlyError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.status >= 500 ? "Something went wrong. Please try again." : err.message;
  }
  if (err instanceof TypeError) {
    // fetch network failures surface as TypeError
    return "Network problem. Check your connection and retry.";
  }
  // Amplify auth errors arrive as plain Error with user-meaningful messages
  // ("Incorrect username or password.", "User is not confirmed.", etc.).
  if (err instanceof Error && err.message) return err.message;
  return "Something went wrong. Please try again.";
}

let activeHouseholdId: string | null = null;
export function setActiveHouseholdId(id: string | null): void {
  activeHouseholdId = id;
}
export function getActiveHouseholdId(): string | null {
  return activeHouseholdId;
}

let onUnauthorized: (() => void) | null = null;
/** AuthProvider registers session-expiry cleanup here; http.ts can't import it (cycle). */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

interface RequestOpts {
  idempotencyKey?: string;
  signal?: AbortSignal;
  unauthenticated?: boolean;
  credentials?: RequestCredentials;
}

// HTTP API JWT authorizers validate the `aud` claim. Cognito access tokens
// don't have `aud` (only `client_id`); ID tokens do. Send the ID token.
async function authHeader(): Promise<Record<string, string>> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function request<T>(
  method: string,
  path: string,
  body: unknown,
  opts: RequestOpts = {},
): Promise<T> {
  const headers: Record<string, string> = {
    accept: "application/json",
  };
  if (body !== undefined) headers["content-type"] = "application/json";
  if (opts.idempotencyKey) headers["idempotency-key"] = opts.idempotencyKey;
  if (activeHouseholdId) headers["x-household-id"] = activeHouseholdId;
  if (!opts.unauthenticated) Object.assign(headers, await authHeader());

  const fetchInit: RequestInit = { method, headers };
  if (body !== undefined) fetchInit.body = JSON.stringify(body);
  if (opts.signal) fetchInit.signal = opts.signal;
  if (opts.credentials) fetchInit.credentials = opts.credentials;
  const res = await fetch(`${getApiBaseUrl()}${path}`, fetchInit);
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string };
    };
    if (res.status === 401 && !opts.unauthenticated) onUnauthorized?.();
    throw new ApiError(
      res.status,
      errBody.error?.code,
      errBody.error?.message ?? res.statusText,
      errBody,
    );
  }
  return (await res.json()) as T;
}

export const apiGet = <T>(path: string, opts?: RequestOpts) =>
  request<T>("GET", path, undefined, opts);
export const apiPost = <T>(path: string, body: unknown, opts?: RequestOpts) =>
  request<T>("POST", path, body, opts);
export const apiPatch = <T>(path: string, body: unknown, opts?: RequestOpts) =>
  request<T>("PATCH", path, body, opts);
export const apiPut = <T>(path: string, body: unknown, opts?: RequestOpts) =>
  request<T>("PUT", path, body, opts);
export const apiDelete = <T>(path: string, opts?: RequestOpts) =>
  request<T>("DELETE", path, undefined, opts);
export const apiDeleteWithBody = <T>(path: string, body: unknown, opts?: RequestOpts) =>
  request<T>("DELETE", path, body, opts);
